import { v4 as uuidv4 } from "uuid";
import useStore from "../../../../Store/Store";
import { logger } from "../../../../utils/Logger";
import {
  SimulationPathTreeBuilder,
  PathTreeNode,
} from "./SimulationPathTreeBuilder";

class SimulationParentCellInserter {
  private static insertionAbortController: AbortController | null = null;

  /**
   * Inserts parent cells for simulation steps in breadth-first manner
   */
  static async insertParentCells(
    simSteps: SimStep[],
    onProgress?: (current: number, total: number) => void
  ): Promise<Map<string, string>> {
    const pathToCellIdMap = new Map<string, string>();

    // Abort any previous insertion
    if (this.insertionAbortController) {
      this.insertionAbortController.abort();
    }

    this.insertionAbortController = new AbortController();
    const signal = this.insertionAbortController.signal;

    try {
      const { repoData } = useStore.getState();

      // Build path tree
      const pathTree = SimulationPathTreeBuilder.buildPathTree(simSteps);
      const levelNodes = SimulationPathTreeBuilder.getNodesByDepth(pathTree);

      let totalNodes = 0;
      levelNodes.forEach((level) => (totalNodes += level.length));
      let processedNodes = 0;

      logger.debug.info(
        `SimulationParentCellInserter: Processing ${totalNodes} parent cells across ${levelNodes.length} levels`
      );

      // Process nodes level by level (breadth-first)
      for (const level of levelNodes) {
        for (const node of level) {
          if (signal.aborted) {
            logger.debug.info("Parent cell insertion was aborted");
            return pathToCellIdMap;
          }

          // Check if this path already has a cell in repoData
          const existingData = repoData[node.path];
          if (existingData?.cellId) {
            pathToCellIdMap.set(node.path, existingData.cellId);
            processedNodes++;
            onProgress?.(processedNodes, totalNodes);
            logger.debug.info(
              `SimulationParentCellInserter: Found existing cell for ${node.path}: ${existingData.cellId}`
            );
            continue;
          }

          // Find the closest parent with a cellId
          const parentCellId = this.findClosestParentCellId(
            node.path,
            repoData,
            pathToCellIdMap
          );

          // Insert the cell
          const cellId = await this.insertSingleParentCell(node, parentCellId);
          if (cellId) {
            pathToCellIdMap.set(node.path, cellId);
          }

          processedNodes++;
          onProgress?.(processedNodes, totalNodes);

          // Add delay to prevent race conditions
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }

      logger.debug.info(
        `SimulationParentCellInserter: Completed processing ${processedNodes} parent cells`
      );
      return pathToCellIdMap;
    } finally {
      this.insertionAbortController = null;
    }
  }

  private static findClosestParentCellId(
    path: string,
    repoData: any,
    pathToCellIdMap: Map<string, string>
  ): string | null {
    const segments = path.split("/").filter(Boolean);

    // Start from immediate parent and go up
    for (let i = segments.length - 2; i >= 0; i--) {
      const parentPath = segments.slice(0, i + 1).join("/");

      // Check if we already created a cell for this parent
      if (pathToCellIdMap.has(parentPath)) {
        return pathToCellIdMap.get(parentPath)!;
      }

      // Check if it exists in repoData
      const parentData = repoData[parentPath];
      if (parentData?.cellId) {
        return parentData.cellId;
      }
    }

    return null;
  }

  private static async insertSingleParentCell(
    node: PathTreeNode,
    parentCellId: string | null
  ): Promise<string | null> {
    const state = useStore.getState();
    const cellId = uuidv4();

    try {
      logger.debug.info(
        `SimulationParentCellInserter: Creating cell for ${
          node.path
        } with parent ${parentCellId || "none"}`
      );

      // Use the same pattern as RepoFile.tsx addCellHandler
      const response = await state.postToDrawioWaitForResponse({
        action: "NEW_LINKED_CELL",
        data: {
          newCellName: node.name,
          parentCellId: parentCellId,
          cellId: cellId,
        },
      });

      if (response?.status === "SUCCESS") {
        const newCellId = response.data?.newCellId || cellId;

        // Create repoData entry
        const newRepoObject = {
          cellName: node.name,
          cellId: newCellId,
          fileName: node.name,
          path: node.path,
          type: node.type,
          visible: true,
          ...(parentCellId && { parentCellId }),
        };

        state.setNewRepoObject(node.path, newRepoObject);
        state.setNewCellToPath(newCellId, node.path);

        // Update parent's children if we have a parent
        if (parentCellId) {
          // Find parent path from pathToCellIdMap or repoData
          const parentPath = this.findParentPathByCellId(
            parentCellId,
            state.repoData
          );
          if (parentPath) {
            const parentData = state.repoData[parentPath];
            if (parentData) {
              const children = parentData.children || [];
              if (!children.includes(node.path)) {
                state.setRepoObjectAttribute(parentPath, "children", [
                  ...children,
                  node.path,
                ]);
              }
            }
          }
        }

        logger.debug.info(
          `SimulationParentCellInserter: Created parent cell for ${node.path}: ${newCellId}`
        );
        return newCellId;
      } else {
        logger.error(
          `SimulationParentCellInserter: Failed to create cell for ${node.path}. Response:`,
          response
        );
      }
    } catch (error) {
      logger.error(
        `SimulationParentCellInserter: Error creating parent cell for ${node.path}:`,
        error
      );
    }

    return null;
  }

  private static findParentPathByCellId(
    cellId: string,
    repoData: any
  ): string | null {
    for (const [path, data] of Object.entries(repoData)) {
      if ((data as any).cellId === cellId) {
        return path;
      }
    }
    return null;
  }

  static abort() {
    if (this.insertionAbortController) {
      this.insertionAbortController.abort();
      this.insertionAbortController = null;
    }
  }
}

// Explicit export
export { SimulationParentCellInserter };
